import React from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { FaExclamationTriangle, FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../styles/ErrorBoundary.css';

/**
 * ErrorBoundary - Componente para capturar errores de React
 * Muestra una UI amigable cuando algo falla en lugar de pantalla en blanco
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log del error para debugging
    console.error('ErrorBoundary capturó un error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Aquí podrías enviar el error a un servicio de logging
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null 
    });
    
    // Redireccionar al inicio
    window.location.href = '/#/home';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container className="error-boundary-container d-flex align-items-center justify-content-center min-vh-100">
          <Card className="error-boundary-card text-center shadow-lg" style={{ maxWidth: '600px' }}>
            <Card.Body className="p-5">
              <FaExclamationTriangle size={64} className="text-warning mb-4" />
              
              <h2 className="mb-3">¡Oops! Algo salió mal</h2>
              
              <p className="text-muted mb-4">
                Lo sentimos, ha ocurrido un error inesperado. No te preocupes, 
                tu información está segura y el equipo técnico ha sido notificado.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Card className="bg-light mb-4 text-start">
                  <Card.Body>
                    <h6 className="text-danger">Detalles del Error (Solo en desarrollo):</h6>
                    <pre className="mb-0 small text-wrap" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {this.state.error.toString()}
                      {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </pre>
                  </Card.Body>
                </Card>
              )}

              <div className="d-flex gap-3 justify-content-center">
                <Button 
                  variant="primary" 
                  onClick={this.handleReset}
                  className="d-flex align-items-center gap-2"
                >
                  <FaHome /> Volver al Inicio
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  onClick={() => window.location.reload()}
                >
                  Recargar Página
                </Button>
              </div>

              <p className="text-muted mt-4 mb-0 small">
                Si el problema persiste, contacta al soporte técnico.
              </p>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorFallback - Componente funcional para usar con react-error-boundary
 * Alternativa más moderna si se prefiere usar la librería
 */
export const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate('/home');
  };

  return (
    <Container className="d-flex align-items-center justify-content-center min-vh-100">
      <Card className="text-center shadow-lg" style={{ maxWidth: '600px' }}>
        <Card.Body className="p-5">
          <FaExclamationTriangle size={64} className="text-warning mb-4" />
          
          <h2 className="mb-3">Servicio No Disponible</h2>
          
          <p className="text-muted mb-4">
            Esta funcionalidad no está disponible en este momento. 
            Por favor, inténtalo más tarde.
          </p>

          {process.env.NODE_ENV === 'development' && error && (
            <Card className="bg-light mb-4 text-start">
              <Card.Body>
                <h6 className="text-danger">Error:</h6>
                <pre className="mb-0 small">{error.message}</pre>
              </Card.Body>
            </Card>
          )}

          <Button 
            variant="primary" 
            onClick={handleGoHome}
            className="d-flex align-items-center gap-2 mx-auto"
          >
            <FaHome /> Volver al Inicio
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ErrorBoundary;
